import React, {ReactNode} from 'react';
import {t} from '@lingui/core/macro';

import ListPage from 'shared/ui/ListPage/ListPage';
import ListToolbar from 'shared/ui/ListToolbar/ListToolbar';
import TableToolbar from 'shared/ui/TableToolbar/TableToolbar';

import SelectAllCheckbox from 'shared/ui/Checkbox/SelectAllCheckbox/SelectAllCheckbox';
import {IMassOperationAction} from 'shared/ui/MassOperations/MassOperations';
import {getCommonMassOperationActions} from 'shared/ui/MassOperations/massOperationActions';
import PlugEmpty from 'shared/ui/Plugs/PlugEmpty/PlugEmpty';
import PlugNoAccess from 'shared/ui/Plugs/PlugNoAccess/PlugNoAccess';
import {ITableProps} from 'shared/ui/Table/Table';
import useOpenState from 'shared/lib/hooks/useOpenState';
import {SEARCH_PARAM} from 'shared/lib/filterHelper';
import {useSearchHandler} from 'shared/lib/hooks/useSearchParamsHandler';
import {useSort} from 'shared/lib/hooks/useSort';

import TagsBar from '../TagsBar/TagsBar';
import {IMonitorListState} from '../../model/useMonitorList';

import MonitorFiltersModal, {IMonitorFiltersFormHandlerProps} from '../MonitorFiltersModal/MonitorFiltersModal';
import {IIncidentsModalState} from '../IncidentsScreen/IncidentsScreen';
import MassOperationsBlock, {IBatchFormProps} from '../MassOperationsBlock/MassOperationsBlock';

export type IMonitorsBatchFormProps = IBatchFormProps;

interface IMonitorsScreenProps<T extends object>
    extends Omit<ITableProps<T>, 'className' | 'emptyStateComponent' | 'setSort'> {
    documentTitle: string,
    title: string,
    count?: number,
    maxCount?: number,
    backUrl?: string,
    isFetching: boolean,
    refetch: () => void,
    searchField?: string,
    FiltersFormHandler: React.ComponentType<IMonitorFiltersFormHandlerProps>,
    renderExportModal: (_state: IIncidentsModalState) => ReactNode,
    list: IMonitorListState,
    extraMassActions?: IMassOperationAction[],
    getBatchHeader: (_actionValue?: string) => string,
    renderBatchForm: (_props: IMonitorsBatchFormProps) => ReactNode,
    summary?: ReactNode,
    createButton: ReactNode,
    createText: string,
    onCreate: () => void,
    children?: ReactNode,
}

function MonitorsScreen<T extends object>({
    documentTitle,
    title,
    count,
    maxCount,
    backUrl,
    isFetching,
    refetch,
    searchField = SEARCH_PARAM,
    FiltersFormHandler,
    renderExportModal,
    list,
    extraMassActions = [],
    getBatchHeader,
    renderBatchForm,
    summary,
    createButton,
    createText,
    onCreate,
    columns,
    children,
    ...tableProps
}: IMonitorsScreenProps<T>) {
    const filter = useOpenState();
    const exportModal = useOpenState();
    const {currentValue: search, onSearchSubmit} = useSearchHandler(searchField);
    const [, setSort] = useSort();
    const {plugState, hasSelectedItems} = list;

    return (
        <ListPage
            {...tableProps}
            documentTitle={documentTitle}
            toolbar={
                <ListToolbar
                    title={title}
                    count={count}
                    maxCount={maxCount}
                    backUrl={backUrl}
                    createButton={createButton}
                    hideCreateButtonOnMobile={plugState === 'noAccess' || plugState === 'empty'}
                    actions={
                        <TableToolbar
                            onSearch={onSearchSubmit}
                            searchValue={search}
                            onFilter={filter.open}
                            onExport={exportModal.open}
                            onRefresh={refetch}
                            isRefreshing={isFetching}
                            disabled={hasSelectedItems}
                        />
                    }
                >
                    <TagsBar disabled={hasSelectedItems}/>
                </ListToolbar>
            }
            actions={
                <MassOperationsBlock
                    list={list}
                    actions={[...getCommonMassOperationActions(), ...extraMassActions]}
                    getHeader={getBatchHeader}
                    renderForm={renderBatchForm}
                >
                    {summary}
                </MassOperationsBlock>
            }
            columns={[
                {
                    label:
                        <SelectAllCheckbox
                            onChange={(e) => list.handleSelectAll(e.target.checked)}
                            checked={list.isSelectAllChecked}
                        />,
                },
                ...columns,
            ]}
            setSort={setSort}
            emptyStateComponent={plugState === 'notFound' ? t`Ничего не найдено` : true}
            plug={
                <>
                    {plugState === 'noAccess' && <PlugNoAccess/>}
                    {plugState === 'empty' && <PlugEmpty onCreate={onCreate} buttonText={createText}/>}
                </>
            }
        >
            <MonitorFiltersModal
                open={filter.isOpen}
                handleClose={filter.close}
                FormHandler={FiltersFormHandler}
            />
            {renderExportModal({open: exportModal.isOpen, handleClose: exportModal.close})}
            {children}
        </ListPage>
    );
}

export default MonitorsScreen;
